/**
 * Jira ticket bodies are ingested as raw Atlassian **wiki markup**, not
 * Markdown, so tokens like `!image.png|width=..!`, `{color}`, `{panel}` reach
 * the Markdown renderer and show as literal text (the 2026-07 "KB images not
 * displaying" report — 2,391 image tokens across Jira chunks). Until the
 * ingest converts Jira wiki markup to Markdown (and ingests the attachments so
 * the screenshots actually render), clean the highest-noise tokens at render
 * time.
 *
 * Deliberately conservative — only unambiguous wiki tokens are touched; real
 * Markdown (including `![alt](url)` images and `attachment://` refs) is left
 * exactly as-is.
 */
export function sanitizeAtlassianWiki(md: string): string {
  if (!md || (!md.includes('!') && !md.includes('{'))) return md;
  return (
    md
      // Unresolved ADF media marker `![alt](jira-attachment://id)` — appears on
      // the live /qa path (ticket fetched, not ingested, so no hash yet). The
      // INGEST path rewrites these to `attachment://<hash>` before storage.
      .replace(/!\[[^\]]*\]\(jira-attachment:\/\/[^)\s]+\)/g, '_[image]_')
      // Wiki image `!name.ext|attrs!` or `!name.ext!` → placeholder. The
      // `(?!\[)` guard leaves real Markdown images `![alt](url)` untouched.
      // We can't show the image (Jira attachments aren't ingested), so a
      // clean placeholder beats a wall of raw markup.
      .replace(/!(?!\[)([^\s!|]+\.(?:png|jpe?g|gif|webp|svg))(?:\|[^!\n]*)?!/gi, '_[image]_')
      // `{color}` / `{color:#fff}` — pure styling wrappers, strip the tokens
      // and keep the inner text.
      .replace(/\{color(?::[^}]*)?\}/gi, '')
      // `{panel...}` / `{panel}` — same, strip the wrapper tokens.
      .replace(/\{panel(?::[^}]*)?\}/gi, '')
  );
}
